/**
 * @fileOverview Utilities for a mutable react state container. A lobotomized
 * Redux for the special use case of integrating a React view into a Backbone
 * view.
 */
import { Events } from 'backbone';
import { extend } from 'underscore';

/**
 * @function composeStore
 * @param stores an array of "view store" functions that expect a store
 * signature `{ getState, updateState, onUpdate, onPublish, publish }` as the
 * first parameter, and will return state to be added to the store.
 * in this way, you can "compose"
 **/

export function composeStore(stores) {
	// state and behavior are the same thing in these stores, since we pass
	// all data and handlers as the `props` collection to react.
	// the initial store has an empty object for state.
	let store = createViewStore();
	// in reverse order, similar to webpack loader resolution order,
	// apply the state returned by each additional "view store" function passed
	stores.reverse().forEach((addition) => {
		// pass the store to each "view store" function, so its handlers
		// have access to the store's `getState`, `updateState`, etc methods
		store.updateState(addition(store));
	});

	return store;
}

export function createViewStore() {
	// use Backbone.Events as our event bus initially, but this could be
	// swapped out with any EventEmitter implementation.
	let bus = extend(Events);
	// slways start with an empty object
	let state = {};
	function getState() {
		// for performance on quick renders, simply return the existing state
		// object; store handlers should just avoid mutating it.
		return state;
	}
	function updateState(props) {
		// create an intermediary variable to store the old state,
		// so we can send an copy of the old state to update events
		const oldState = extend({}, state);
		// like Object.assign, underscore.extend mutates in place
		extend(state, props);
		// update handlers are called with three arguments:
		// the new state, the portion of state that was passed to change
		// (some of which may not have changed), and the old state before
		// the change took place.
		bus.trigger('update', state, props, oldState);
	}
	function onUpdate(handler) {
		// should run every time the state is updated, implemented above
		// simply delegate to the EventEmitter here
		bus.on('update', handler);
	}
	function onPublish(handler) {
		// the `publish` event is an arbitrary signal that the store should
		// dispatch a change to its environment.
		// simply delegate to the EventEmitter here
		bus.on('publish', handler);
	}
	function publish(...args) {
		// this is the only way to trigger the `publish` event. it simply
		// signals that the store should interact with its surroundings.
		// subscribe by calling .onPublish and then affect outer state in
		// that handler.
		bus.trigger('publish', ...args);
	}
	return { getState, updateState, onUpdate, onPublish, publish };
}



// WEBPACK FOOTER //
// ./view/activity/dateRangePicker/storeUtils.es6